import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUser, faCheckCircle, faInfoCircle, faSpinner, faPlus, faBuilding, faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import toastService from '@frontend/AppSettings/services/ToastService';
import './AddContactPage.css';
import FormInput from '@frontend/Components/Forms/FormInput/FormInput';

interface AddContactPageProps {
  onClose: () => void;
  stakeholderId: string;
  type: 'individual' | 'organization';
  stakeholderData?: any;
  onContactAdded?: () => void;
}

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  company: string;
  address: string;
  isPrimaryContact: boolean;
}

const AddContactPage: React.FC<AddContactPageProps> = ({ 
  onClose, 
  stakeholderId, 
  type,
  stakeholderData,
  onContactAdded 
}) => {
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    company: stakeholderData?.Company || '',
    address: '',
    isPrimaryContact: false,
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // Pre-fill company if this is an organization stakeholder
    if (type === 'organization' && stakeholderData?.Company) {
      setFormData(prev => ({
        ...prev,
        company: stakeholderData.Company,
      }));
    }
  }, [type, stakeholderData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, isPrimaryContact: e.target.checked });
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      title: '',
      company: stakeholderData?.Company || '',
      address: '',
      isPrimaryContact: false,
    });
    setError('');
    onClose();
  };

  const handleContactCreation = async () => {
    setLoading(true);
    setError('');

    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setLoading(false);
      setError('Please fill in all required fields (First Name, Last Name, Email).');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setLoading(false);
      setError('Please enter a valid email address.');
      return;
    }

    try {
      // Determine if we should create a client or agent
      // For organizations, we'll create a client/agent with the same company
      // For individuals, we'll create a client/agent with the stakeholder's company if they have one
      const contactData: any = {
        FirstName: formData.firstName,
        LastName: formData.lastName,
        Email: formData.email,
        Phone: formData.phone || '',
        Company: formData.company || '',
        Title: formData.title || '',
        Address: formData.address || '',
      };

      let success = false;
      let createdContactId: string | null = null;

      // Create as client (you can adjust this logic based on your business rules)
      try {
        const clientResponse = await ClientService.postClient(contactData);
        if (clientResponse) {
          success = true;
          // Get the created client ID from the response or fetch it
          const clients = await ClientService.getClients();
          const createdClient = clients.find(
            (c: any) => 
              c.Email === formData.email && 
              c.FirstName === formData.firstName && 
              c.LastName === formData.lastName
          );
          if (createdClient) {
            createdContactId = createdClient.ClientID;
          }
        }
      } catch (clientError) {
        console.error('Error creating client:', clientError);
        // Try creating as agent if client creation fails
        try {
          const agentResponse = await AgentsService.addAgent(contactData);
          if (agentResponse) {
            success = true;
            const agents = await AgentsService.getAgents();
            const createdAgent = agents.find(
              (a: any) => 
                a.Email === formData.email && 
                a.FirstName === formData.firstName && 
                a.LastName === formData.lastName
            );
            if (createdAgent) {
              createdContactId = createdAgent.AgentID;
            }
          }
        } catch (agentError) {
          console.error('Error creating agent:', agentError);
          throw new Error('Failed to create contact. Please try again.');
        }
      }

      if (success) {
        // If marked as primary contact and this is an organization stakeholder
        if (formData.isPrimaryContact && type === 'organization' && formData.company && createdContactId) {
          // Note: The primary contact in SEPTOrbit is derived from the first contact found with that company name.
          // To ensure this contact becomes the primary contact, we would need to either:
          // 1. Update a primary contact field in the database (requires backend changes)
          // 2. Ensure this contact is returned first when querying (may require sorting/ordering)
          // For now, we'll show a success message and the organization list will refresh to show the new contact.
          // The primary contact will update automatically when the page refreshes if this contact becomes the first one.
          toastService.success('Contact added successfully. The organization list will update to reflect the new primary contact.');
        } else {
          toastService.success('Contact added successfully');
        }

        // Trigger refresh callback if provided
        if (onContactAdded) {
          onContactAdded();
        }

        // Close modal
        handleClose();
      } else {
        throw new Error('Failed to create contact. Please try again.');
      }
    } catch (error: any) {
      console.error('Error adding contact:', error);
      setError(error.message || 'Failed to add the contact. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-contact-overlay" onClick={handleClose}>
      <div className="add-contact-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="add-contact-header">
          <div className="add-contact-header-left">
            <div className="add-contact-title-row">
              <div className="add-contact-header-icon">
                <FontAwesomeIcon icon={faPlus} />
              </div>
              <div className="add-contact-header-text">
                <h2 className="add-contact-title">Add New Contact</h2>
                <p className="add-contact-subtitle">Add a new contact to this stakeholder</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="add-contact-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="add-contact-content">
          {/* Basic Information Section */}
          <div className="add-contact-section add-contact-section-blue">
            <div className="add-contact-section-header">
              <div className="add-contact-section-icon-wrapper add-contact-icon-blue">
                <FontAwesomeIcon icon={faUser} />
              </div>
              <h3 className="add-contact-section-title">Basic Information</h3>
            </div>
            <div className="add-contact-section-content">
              <div className="add-contact-form-row">
                <FormInput
                  label="First Name*"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="e.g., John"
                  required
                />
                <FormInput
                  label="Last Name*"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="e.g., Smith"
                  required
                />
              </div>
              <FormInput
                label="Email*"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="e.g., john.smith@example.com"
                required
              />
              <FormInput
                label="Phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="e.g., (555) 123-4567"
              />
            </div>
          </div>

          {/* Professional Details Section */}
          <div className="add-contact-section add-contact-section-purple">
            <div className="add-contact-section-header">
              <div className="add-contact-section-icon-wrapper add-contact-icon-purple">
                <FontAwesomeIcon icon={faBuilding} />
              </div>
              <h3 className="add-contact-section-title">Professional Details</h3>
            </div>
            <div className="add-contact-section-content">
              <FormInput
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Chief Executive Officer, General Counsel"
              />
              <FormInput
                label="Company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="e.g., Acme Corporation"
                disabled={type === 'organization' && !!stakeholderData?.Company}
              />
              {type === 'organization' && stakeholderData?.Company && (
                <div className="add-contact-info-box">
                  <FontAwesomeIcon icon={faInfoCircle} className="add-contact-info-icon" />
                  <span>Company is set to match the organization</span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Information Section */}
          <div className="add-contact-section add-contact-section-green">
            <div className="add-contact-section-header">
              <div className="add-contact-section-icon-wrapper add-contact-icon-green">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <h3 className="add-contact-section-title">Additional Information</h3>
            </div>
            <div className="add-contact-section-content">
              <FormInput
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="e.g., 123 Main St, City, State, ZIP"
              />
            </div>
          </div>

          {/* Primary Contact Section */}
          {type === 'organization' && (
            <div className="add-contact-section add-contact-section-white">
              <div className="add-contact-section-header">
                <h3 className="add-contact-section-title">Primary Contact</h3>
              </div>
              <div className="add-contact-section-content">
                <div className="add-contact-checkbox-wrapper">
                  <label className="add-contact-checkbox-label">
                    <input
                      type="checkbox"
                      name="isPrimaryContact"
                      checked={formData.isPrimaryContact}
                      onChange={handleCheckboxChange}
                      className="add-contact-checkbox"
                    />
                    <span className="add-contact-checkbox-text">
                      Set as primary contact for this organization
                    </span>
                  </label>
                  <div className="add-contact-info-box">
                    <FontAwesomeIcon icon={faInfoCircle} className="add-contact-info-icon" />
                    <span>
                      If checked, this contact will be set as the primary contact for the organization and will automatically update in the organization list.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && <div className="add-contact-error">{error}</div>}
        </div>

        {/* Footer Actions */}
        <div className="add-contact-footer">
          <div className="add-contact-footer-info">
            <FontAwesomeIcon icon={faInfoCircle} className="add-contact-footer-info-icon" />
            <span>All required fields must be completed</span>
          </div>
          <div className="add-contact-footer-buttons">
            <button
              type="button"
              className="add-contact-button add-contact-button-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="add-contact-button add-contact-button-primary"
              onClick={handleContactCreation}
              disabled={loading}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Adding...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Add Contact
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddContactPage;

